// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { SEED_ORG_PLACEHOLDER, SEED_WORKSPACE_PLACEHOLDER } from './constants'
import type { SeedSchema } from './constants'

/**
 * Seed schema for the dashboards virtual collection.
 * Stores JSON-driven dashboard configurations.
 */
export const dashboardsSchema: SeedSchema = {
  name: 'dashboards',
  title: 'Dashboards',
  description: 'JSON-driven configurable dashboard configurations',
  category: 'dashboard',
  target: 'records',
  titleField: 'title',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'dash' },
  defaultSort: { field: 'sortOrder', direction: 'asc' },
  fields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Dashboard name' },
    },
    {
      name: 'slug',
      label: 'Slug',
      type: 'text',
      required: true,
      order: 2,
      config: { placeholder: 'url-friendly-identifier' },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
      required: false,
      order: 3,
      config: { placeholder: 'Optional description' },
    },
    {
      name: 'scopeType',
      label: 'Scope Type',
      type: 'select',
      required: true,
      order: 4,
      options: [
        { label: 'Organization', value: 'org' },
        { label: 'Workspace', value: 'workspace' },
        { label: 'Project', value: 'project' },
      ],
    },
    {
      name: 'scopeId',
      label: 'Scope ID',
      type: 'text',
      required: false,
      order: 5,
      config: { placeholder: 'Workspace or project ID' },
    },
    {
      name: 'userId',
      label: 'Owner User ID',
      type: 'text',
      required: false,
      order: 6,
      config: { placeholder: 'Owner for personal dashboards' },
    },
    {
      name: 'visibility',
      label: 'Visibility',
      type: 'select',
      required: true,
      order: 7,
      options: [
        { label: 'Private', value: 'private' },
        { label: 'Team', value: 'team' },
        { label: 'Public', value: 'public' },
      ],
    },
    {
      name: 'layout',
      label: 'Layout',
      type: 'json',
      required: true,
      order: 8,
      config: {},
    },
    {
      name: 'dataSources',
      label: 'Data Sources',
      type: 'json',
      required: true,
      order: 9,
      config: {},
    },
    {
      name: 'hooks',
      label: 'Hooks',
      type: 'json',
      required: false,
      order: 10,
      config: {},
    },
    {
      name: 'interactions',
      label: 'Interactions',
      type: 'json',
      required: false,
      order: 11,
      config: {},
    },
    {
      name: 'templateId',
      label: 'Source Template',
      type: 'text',
      required: false,
      order: 12,
      config: {},
    },
    {
      name: 'builtIn',
      label: 'Built-In',
      type: 'boolean',
      required: true,
      order: 13,
      config: {},
    },
    {
      name: 'isDefault',
      label: 'Is Default',
      type: 'boolean',
      required: false,
      order: 14,
      config: {},
    },
    {
      name: 'sortOrder',
      label: 'Sort Order',
      type: 'number',
      required: false,
      order: 15,
      config: {},
    },
  ],
}

/**
 * Seed schema for the dashboardtemplates virtual collection.
 * Stores reusable dashboard template configurations.
 */
export const dashboardtemplatesSchema: SeedSchema = {
  name: 'dashboardtemplates',
  title: 'Dashboard Templates',
  description: 'Reusable dashboard template configurations for cloning',
  category: 'dashboard',
  target: 'records',
  titleField: 'title',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'dtpl' },
  defaultSort: { field: 'title', direction: 'asc' },
  fields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Template name' },
    },
    {
      name: 'slug',
      label: 'Slug',
      type: 'text',
      required: true,
      order: 2,
      config: { placeholder: 'url-friendly-identifier' },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
      required: false,
      order: 3,
      config: { placeholder: 'What this template provides' },
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      required: true,
      order: 4,
      options: [
        { label: 'Projects', value: 'projects' },
        { label: 'Sales', value: 'sales' },
        { label: 'Marketing', value: 'marketing' },
        { label: 'Finance', value: 'finance' },
        { label: 'SaaS', value: 'saas' },
        { label: 'General', value: 'general' },
      ],
    },
    {
      name: 'targetScope',
      label: 'Target Scope',
      type: 'select',
      required: true,
      order: 5,
      options: [
        { label: 'Organization', value: 'org' },
        { label: 'Workspace', value: 'workspace' },
        { label: 'Project', value: 'project' },
      ],
    },
    {
      name: 'layout',
      label: 'Layout',
      type: 'json',
      required: true,
      order: 6,
      config: {},
    },
    {
      name: 'dataSources',
      label: 'Data Sources',
      type: 'json',
      required: true,
      order: 7,
      config: {},
    },
    {
      name: 'hooks',
      label: 'Hooks',
      type: 'json',
      required: false,
      order: 8,
      config: {},
    },
    {
      name: 'interactions',
      label: 'Interactions',
      type: 'json',
      required: false,
      order: 9,
      config: {},
    },
    {
      name: 'requiredComponents',
      label: 'Required Components',
      type: 'json',
      required: false,
      order: 10,
      config: {},
    },
    {
      name: 'requiredCollections',
      label: 'Required Collections',
      type: 'json',
      required: false,
      order: 11,
      config: {},
    },
    {
      name: 'builtIn',
      label: 'Built-In',
      type: 'boolean',
      required: true,
      order: 12,
      config: {},
    },
    {
      name: 'previewImageUrl',
      label: 'Preview Image URL',
      type: 'text',
      required: false,
      order: 13,
      config: { placeholder: 'Preview screenshot URL' },
    },
  ],
}

/**
 * Seed schema for the dashboardcomponents virtual collection.
 * Stores component manifests for discovery by LLM agents and the dashboard editor.
 */
export const dashboardcomponentsSchema: SeedSchema = {
  name: 'dashboardcomponents',
  title: 'Dashboard Components',
  description: 'Component manifests for dashboard plugins',
  category: 'dashboard',
  target: 'records',
  titleField: 'name',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'dcmp' },
  defaultSort: { field: 'name', direction: 'asc' },
  fields: [
    {
      name: 'componentId',
      label: 'Component ID',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Namespaced ID (e.g., fs.data-table)' },
    },
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      order: 2,
      config: { placeholder: 'Display name' },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'text',
      required: false,
      order: 3,
      config: { placeholder: 'What this component does' },
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      required: true,
      order: 4,
      options: [
        { label: 'Layout', value: 'layout' },
        { label: 'View', value: 'view' },
        { label: 'UI Element', value: 'ui' },
      ],
    },
    {
      name: 'icon',
      label: 'Icon',
      type: 'text',
      required: false,
      order: 5,
      config: { placeholder: 'Lucide icon name' },
    },
    {
      name: 'propsSchema',
      label: 'Props Schema',
      type: 'json',
      required: true,
      order: 6,
      config: {},
    },
    {
      name: 'dataSchema',
      label: 'Data Schema',
      type: 'json',
      required: false,
      order: 7,
      config: {},
    },
    {
      name: 'slots',
      label: 'Slots',
      type: 'json',
      required: false,
      order: 8,
      config: {},
    },
    {
      name: 'events',
      label: 'Events',
      type: 'json',
      required: false,
      order: 9,
      config: {},
    },
    {
      name: 'constraints',
      label: 'Constraints',
      type: 'json',
      required: false,
      order: 10,
      config: {},
    },
    {
      name: 'source',
      label: 'Source',
      type: 'select',
      required: true,
      order: 11,
      options: [
        { label: 'Built-In', value: 'builtin' },
        { label: 'Marketplace', value: 'marketplace' },
        { label: 'User', value: 'user' },
      ],
    },
    {
      name: 'pluginId',
      label: 'Plugin ID',
      type: 'text',
      required: false,
      order: 12,
      config: { placeholder: 'Marketplace plugin ID' },
    },
    {
      name: 'builtIn',
      label: 'Built-In',
      type: 'boolean',
      required: true,
      order: 13,
      config: {},
    },
  ],
}

export const dashboardSeeds: SeedSchema[] = [
  dashboardsSchema,
  dashboardtemplatesSchema,
  dashboardcomponentsSchema,
]
